import { Button, Input, message } from 'antd'
import React, { useEffect, useState } from 'react'
import { patchCrowsNestOpportunity } from 'src/utils/crowsNestApi'
import { mapOpportunityFeedItem } from 'src/utils/crowsNestOpportunityFeed'
import { CROWS_NEST_TOOLTIPS } from 'src/utils/crowsNestTooltips'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled from 'styled-components/macro'
import CrowsNestTipLabel from './CrowsNestTipLabel'

/**
 * Expanded Crow’s Nest row — follow-up context + editable sales note.
 */
const CrowsNestOpportunityExpand = ({ row, onUpdated }) => {
  const [noteDraft, setNoteDraft] = useState(row.salesNote || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setNoteDraft(row.salesNote || '')
  }, [row.id, row.salesNote])

  const saveNote = async () => {
    if (!row.id) return
    setSaving(true)
    try {
      const response = await patchCrowsNestOpportunity(row.id, {
        salesNote: noteDraft,
      })
      const mapped = mapOpportunityFeedItem(response.opportunity)
      if (mapped && onUpdated) onUpdated(mapped)
      message.success('Note saved')
    } catch (err) {
      message.error(
        crowsNestErrorMessage(err, "Couldn't save the note. Try again.", {
          range: false,
        }),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Panel data-testid={`opportunity-expand-${row.id}`}>
      {row.whyFired ? (
        <DetailBlock>
          <DetailLabel>
            <CrowsNestTipLabel
              label='Why this fired'
              tip={CROWS_NEST_TOOLTIPS.whyFired}
            />
          </DetailLabel>
          <DetailText data-testid={`opportunity-why-${row.id}`}>
            {row.whyFired}
          </DetailText>
        </DetailBlock>
      ) : null}

      {row.cooldownHint ? (
        <DetailBlock>
          <DetailLabel>
            <CrowsNestTipLabel
              label='Cooldown'
              tip={CROWS_NEST_TOOLTIPS.cooldownHint}
            />
          </DetailLabel>
          <CooldownText
            $active={row.cooldownActive}
            data-testid={`opportunity-cooldown-${row.id}`}
          >
            {row.cooldownHint}
          </CooldownText>
        </DetailBlock>
      ) : null}

      <DetailBlock>
        <DetailLabel>
          <CrowsNestTipLabel
            label='Suggested follow-up'
            tip={CROWS_NEST_TOOLTIPS.suggestedFollowUp}
          />
        </DetailLabel>
        <DetailText>{row.suggestedFollowUp}</DetailText>
      </DetailBlock>

      <DetailBlock>
        <DetailLabel>
          <CrowsNestTipLabel
            label='Sales email'
            tip={CROWS_NEST_TOOLTIPS.salesEmail}
          />
        </DetailLabel>
        <DetailText>{row.salesEmailLabel}</DetailText>
      </DetailBlock>

      <DetailBlock>
        <DetailLabel>
          <CrowsNestTipLabel
            label='Sales note'
            tip={CROWS_NEST_TOOLTIPS.salesNote}
          />
        </DetailLabel>
        <Input.TextArea
          value={noteDraft}
          onChange={event => setNoteDraft(event.target.value)}
          maxLength={280}
          rows={2}
          placeholder='e.g. Left voicemail — calling back after lunch'
          data-testid={`opportunity-note-${row.id}`}
        />
        <NoteFooter>
          <CharCount>{String(noteDraft || '').length}/280</CharCount>
          <Button
            type='primary'
            size='small'
            loading={saving}
            disabled={noteDraft === (row.salesNote || '')}
            onClick={saveNote}
            data-testid={`opportunity-note-save-${row.id}`}
          >
            Save note
          </Button>
        </NoteFooter>
      </DetailBlock>
    </Panel>
  )
}

const Panel = styled.div`
  display: grid;
  gap: 12px;
  max-width: 720px;
  padding: 4px 8px 8px;
`

const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DetailLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
  font-weight: 600;
`

const DetailText = styled.div`
  color: ${({ theme }) => theme.colours.bbqBlack || '#1a1a1a'};
  font-size: 13px;
  white-space: normal;
`

const CooldownText = styled.div`
  color: ${({ $active, theme }) =>
    $active
      ? theme.colours.tomatoRed || '#b33a3a'
      : theme.colours.seaweedGreen || '#2f6b3a'};
  font-size: 13px;
  white-space: normal;
`

const NoteFooter = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 6px;
`

const CharCount = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 11px;
  margin-right: auto;
`

export default CrowsNestOpportunityExpand
